import React from "react";

import {
  getSeatButtonShape,
  getSeatShapeFillStyle,
  getSeatShapeFrameStyle,
  getSeatShapeStyle,
  getSeatShapeTextStyle,
} from "./seatLayoutShapes.mjs";


const STATUS_STYLE = {
  available: {
    background: "#ffffff",
    color: "#111827",
    border: "1px solid rgba(15, 23, 42, 0.14)",
    opacity: 1,
  },
  selected: {
    background: "#0f172a",
    color: "#ffffff",
    border: "1px solid #0f172a",
    opacity: 1,
  },
  reserved: {
    background: "#e5e7eb",
    color: "#9ca3af",
    border: "1px solid #d1d5db",
    opacity: 0.95,
  },
  maintenance: {
    background: "#fde68a",
    color: "#92400e",
    border: "1px solid #fbbf24",
    opacity: 0.95,
  },
  unavailable: {
    background: "#f3f4f6",
    color: "#9ca3af",
    border: "1px solid #e5e7eb",
    opacity: 0.9,
  },
  inactive: {
    background: "#cbd5e1",
    color: "#64748b",
    border: "1px solid #94a3b8",
    opacity: 0.8,
  },
};


export function SeatLayoutLegend({ className = "" }) {
  return (
    <div className={`mt-4 flex flex-wrap gap-3 text-xs ${className}`.trim()}>
      <div className="flex items-center gap-2 text-gray-500">
        <span className="h-3 w-3 rounded-full border border-gray-300 bg-white" /> 가능
      </div>
      <div className="flex items-center gap-2 text-gray-900">
        <span className="h-3 w-3 rounded-full bg-slate-900" /> 선택
      </div>
      <div className="flex items-center gap-2 text-gray-500">
        <span className="h-3 w-3 rounded-full bg-gray-200" /> 예약됨
      </div>
      <div className="flex items-center gap-2 text-amber-700">
        <span className="h-3 w-3 rounded-full bg-amber-300" /> 점검중
      </div>
    </div>
  );
}


function SeatLayoutCanvas({
  layout,
  selectedSeatIds = [],
  onSelectSeat,
  readOnly = false,
  showLegend = true,
  className = "",
  canvasClassName = "",
  autoFit = false,
  bare = false,
}) {
  const tierColors = React.useMemo(() => {
    return layout.packages.reduce((acc, item) => {
      acc[item.id] = item.color;
      return acc;
    }, {});
  }, [layout.packages]);

  // 좌석이 실제로 사용하는 구역(layout_package)만 범례에 노출한다.
  const legendTiers = React.useMemo(() => {
    const used = new Set((layout.seats || []).map((s) => s.layout_package).filter((v) => v != null));
    return (layout.packages || [])
      .filter((p) => p && p.color && used.has(p.id))
      .slice()
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [layout]);

  // autoFit: 좌석이 캔버스 한쪽에 몰려 작게 나올 때, 좌석들의 경계(bounding box)에 맞춰
  // 확대해 빈 여백을 제거한다. 실제 상대 위치는 유지된다. (기본 false → 상세 페이지 동작 불변)
  const viewBox = React.useMemo(() => {
    const cw = layout.canvas_width || 1;
    const ch = layout.canvas_height || 1;
    const fullBox = { x: 0, y: 0, width: cw, height: ch };
    if (!autoFit) return fullBox;
    const seats = (layout.seats || []).filter((s) => s && s.is_active !== false);
    if (!seats.length) return fullBox;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    seats.forEach((s) => {
      minX = Math.min(minX, s.x);
      minY = Math.min(minY, s.y);
      maxX = Math.max(maxX, s.x + s.width);
      maxY = Math.max(maxY, s.y + s.height);
    });
    const rawW = Math.max(maxX - minX, 1);
    const rawH = Math.max(maxY - minY, 1);
    const padX = rawW * 0.08;
    const padY = rawH * 0.08;
    const x = Math.min(Math.max(0, minX - padX), cw);
    const y = Math.min(Math.max(0, minY - padY), ch);
    const width = Math.max(1, Math.min(rawW + padX * 2, cw - x));
    const height = Math.max(1, Math.min(rawH + padY * 2, ch - y));
    return { x, y, width, height };
  }, [autoFit, layout]);

  // 배경 이미지는 원래 캔버스 전체 기준이므로, autoFit 으로 잘라볼 때 해당 영역만 보이도록 보정한다.
  const hasBackgroundImage = Boolean(layout.background_image_url);
  let backgroundSize = "cover";
  let backgroundPosition = "center";
  if (autoFit && hasBackgroundImage) {
    const cw = layout.canvas_width || 1;
    const ch = layout.canvas_height || 1;
    const spanX = cw - viewBox.width;
    const spanY = ch - viewBox.height;
    backgroundSize = `${(cw / viewBox.width) * 100}% ${(ch / viewBox.height) * 100}%`;
    backgroundPosition = `${spanX > 0 ? (viewBox.x / spanX) * 100 : 0}% ${spanY > 0 ? (viewBox.y / spanY) * 100 : 0}%`;
  }

  return (
    <div className={className}>
      <div
        className={`relative overflow-hidden ${bare ? "bg-[#faf7ef]" : "rounded-3xl border border-gray-200 bg-[#faf7ef] shadow-sm"} ${canvasClassName}`.trim()}
      >
        <div
          className="relative"
          style={{
            width: "100%",
            aspectRatio: `${viewBox.width} / ${viewBox.height}`,
            backgroundImage: hasBackgroundImage ? `linear-gradient(rgba(255,255,255,0.18), rgba(255,255,255,0.18)), url(${layout.background_image_url})` : "radial-gradient(circle at top, rgba(255,255,255,0.9), rgba(244,239,227,0.92))",
            backgroundSize,
            backgroundPosition,
            backgroundRepeat: "no-repeat",
          }}
        >
          {layout.seats.map((seat) => {
            const isSelected = selectedSeatIds.includes(seat.spot_id);
            const styleKey = isSelected ? "selected" : (seat.availability?.status || "unavailable");
            const style = STATUS_STYLE[styleKey] || STATUS_STYLE.unavailable;
            const accent = tierColors[seat.layout_package] || "#111827";
            const isClickable = !readOnly && Boolean(onSelectSeat) && Boolean(seat.availability?.can_select);
            const seatShape = getSeatButtonShape(seat.meta);
            const shapeTextStyle = getSeatShapeTextStyle(seatShape);
            const shapeStyle = getSeatShapeStyle(seatShape);

            return (
              <button
                key={seat.id}
                type="button"
                onClick={() => isClickable && onSelectSeat(seat)}
                disabled={!readOnly && !isClickable}
                title={seat.label || seat.spot_name}
                style={{
                  position: "absolute",
                  left: `${((seat.x - viewBox.x) / viewBox.width) * 100}%`,
                  top: `${((seat.y - viewBox.y) / viewBox.height) * 100}%`,
                  width: `${(seat.width / viewBox.width) * 100}%`,
                  height: `${(seat.height / viewBox.height) * 100}%`,
                  transform: `rotate(${seat.rotation || 0}deg)`,
                  background: style.background,
                  color: style.color,
                  border: style.border,
                  opacity: style.opacity,
                  boxShadow: isSelected ? "0 16px 30px rgba(15, 23, 42, 0.18)" : "0 8px 16px rgba(15, 23, 42, 0.08)",
                  padding: 0,
                  overflow: "hidden",
                  cursor: isClickable ? "pointer" : "default",
                  ...shapeStyle,
                  ...getSeatShapeFrameStyle(seatShape, accent),
                }}
              >
                {seatShape ? (
                  <span
                    style={{
                      position: "absolute",
                      ...getSeatShapeFillStyle(seatShape, style),
                      ...shapeStyle,
                    }}
                  />
                ) : (
                  <span
                    style={{
                      position: "absolute",
                      inset: 0,
                      borderTop: `4px solid ${accent}`,
                      borderRadius: 18,
                    }}
                  />
                )}
                <span
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    height: "100%",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "clamp(10px, 1.4vw, 14px)",
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: "0.02em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    ...shapeTextStyle,
                  }}
                >
                  {seat.label || seat.spot_name}
                </span>
              </button>
            );
          })}
        </div>

        {legendTiers.length ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-200/70 bg-white/85 px-3 py-2 text-xs backdrop-blur-sm">
            <span className="font-semibold text-gray-500">구역</span>
            {legendTiers.map((tier) => (
              <div key={tier.id} className="flex items-center gap-1.5 text-gray-600">
                <span className="h-3 w-3 rounded-[4px] bg-white" style={{ border: `2px solid ${tier.color}` }} />
                {tier.name}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {showLegend ? <SeatLayoutLegend /> : null}
    </div>
  );
}

export default SeatLayoutCanvas;
